import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const { FiMenu, FiX, FiHome, FiUsers, FiFolder, FiLayers, FiFileText, FiSettings, FiShield, FiScale, FiTrendingUp, FiUserCheck, FiAlertTriangle, FiBarChart3, FiChevronDown, FiTarget, FiLogOut, FiUser } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Base navigation items - some are admin-only
  const allNavigationItems = [
    // Dashboard removed as Logo serves this purpose
    { id: 'staff', name: 'Staff', path: '/staff', icon: FiUsers, description: 'Staff directory & profiles' },
    { id: 'cases', name: 'Cases', path: '/cases', icon: FiFolder, description: 'Individual case tracking' },
    { id: 'workflows', name: 'Workflows', path: '/company-workflows', icon: FiLayers, description: 'Organizational processes' },
    { id: 'team', name: 'Team', path: '/team', icon: FiUsers, description: 'Manage team members', adminOnly: true },
    {
      id: 'hr-tools', name: 'HR Tools', icon: FiFileText, description: 'HR Processes & Relations', dropdown: [
        { name: 'Recruitment', path: '/recruitment', icon: FiUsers },
        { name: 'Onboarding', path: '/onboarding', icon: FiUserCheck },
        { name: 'Core Lifecycle', path: '/core-lifecycle', icon: FiSettings },
        { name: 'Performance', path: '/performance', icon: FiTrendingUp },
        { name: 'Disciplinary & Grievance', path: '/employee-relations', icon: FiAlertTriangle },
        { name: 'Exit Processes', path: '/exit-processes', icon: FiScale },
        { name: 'Tribunal Prep', path: '/tribunal-prep', icon: FiScale }
      ]
    },
    { id: 'compliance', name: 'Compliance', path: '/compliance', icon: FiShield, description: 'Legal compliance tracking' }
  ];

  // Filter navigation items based on role
  const navigationItems = allNavigationItems.filter(item => !item.adminOnly || isAdmin);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const isDropdownActive = (dropdown) => {
    return dropdown.some(item => isActive(item.path));
  };

  const toggleDropdown = (itemId) => {
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  return (
    <>
      {/* Desktop Navbar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                <div className="flex items-center justify-center">
                  <SafeIcon icon={FiShield} className="text-blue-600 text-3xl" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-xl font-bold text-gray-900">HR Compliance</h1>
                  <p className="text-xs text-gray-500">UK Employment System</p>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <div key={item.id} className="relative">
                  {item.dropdown ? (
                    <div className="relative">
                      <button
                        onClick={() => toggleDropdown(item.id)}
                        className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isDropdownActive(item.dropdown)
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                      >
                        <SafeIcon icon={item.icon} className="text-lg" />
                        <span className="hidden xl:block">{item.name}</span>
                        <SafeIcon icon={FiChevronDown} className="text-sm" />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === item.id && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50"
                          >
                            {item.dropdown.map((subItem) => (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                onClick={() => setActiveDropdown(null)}
                                className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${isActive(subItem.path)
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                              >
                                <SafeIcon icon={subItem.icon} />
                                <span>{subItem.name}</span>
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(item.path)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      <SafeIcon icon={item.icon} className="text-lg" />
                      <span className="hidden xl:block">{item.name}</span>
                    </Link>
                  )}
                </div>
              ))}

              {/* User Menu - Desktop */}
              {user && (
                <div className="ml-4 pl-4 border-l border-gray-200 flex items-center space-x-1">
                  <Link
                    to="/profile"
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive('/profile')
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    title="My Profile"
                  >
                    <SafeIcon icon={FiUser} className="text-lg" />
                    <span className="hidden xl:block">Profile</span>
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                    title="Sign Out"
                  >
                    <SafeIcon icon={FiLogOut} className="text-lg" />
                    <span className="hidden xl:block">Sign Out</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-gray-600 hover:text-gray-900 focus:outline-none focus:text-gray-900 p-2"
              >
                <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-200"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigationItems.map((item) => (
                  <div key={item.id}>
                    {item.dropdown ? (
                      <>
                        <button
                          onClick={() => toggleDropdown(item.id)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium transition-colors ${isDropdownActive(item.dropdown)
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center space-x-3">
                            <SafeIcon icon={item.icon} />
                            <span>{item.name}</span>
                          </div>
                          <SafeIcon
                            icon={FiChevronDown}
                            className={`transition-transform ${activeDropdown === item.id ? 'rotate-180' : ''
                              }`}
                          />
                        </button>
                        <AnimatePresence>
                          {activeDropdown === item.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="ml-6 space-y-1"
                            >
                              {item.dropdown.map((subItem) => (
                                <Link
                                  key={subItem.path}
                                  to={subItem.path}
                                  onClick={() => {
                                    setIsOpen(false);
                                    setActiveDropdown(null);
                                  }}
                                  className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive(subItem.path)
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                                >
                                  <SafeIcon icon={subItem.icon} />
                                  <span>{subItem.name}</span>
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium transition-colors ${isActive(item.path)
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                      >
                        <SafeIcon icon={item.icon} />
                        <span>{item.name}</span>
                      </Link>
                    )}
                  </div>
                ))}

                {/* User Menu - Mobile */}
                {user && (
                  <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                    <Link
                      to="/profile"
                      onClick={() => setIsOpen(false)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium transition-colors ${isActive('/profile')
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                    >
                      <SafeIcon icon={FiUser} />
                      <span>My Profile</span>
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <SafeIcon icon={FiLogOut} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Click outside to close dropdown */}
      {activeDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setActiveDropdown(null)}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setActiveDropdown(null);
            }
          }}
        />
      )}
    </>
  );
};

export default Navbar;